<script setup lang="ts">
/**
 * NavLinkItem — 헤더 네비게이션 항목 1건(데스크톱 메가메뉴 · 모바일 아코디언 공용).
 *
 * ## 왜 분리했나
 *
 * `NavLink` 의 목적지는 4종(disabled / href / section / route)인데, 과거에는 이 분기가
 * AppHeader 안에 **데스크톱·모바일 각각 한 벌씩** 있었고 배지 렌더까지 포함하면 사실상
 * 8곳이 같은 모양을 반복했다. 링크 종류를 하나 늘리면 8곳을 고쳐야 하는 구조였다.
 * 여기서는 `<component :is>` 로 **한 벌**로 합치고, 데스크톱/모바일 차이는 `variant` 로만 가른다.
 *
 * ## ⚠️ 상태 컴포저블을 여기서 호출하지 않는다
 *
 * 이동 후에는 열린 패널(`useGnbMenu`)과 모바일 메뉴(`useMobileNav`)를 닫아야 하는데,
 * **`useMobileNav` 는 인스턴스 로컬 ref 라 전역 싱글턴이 아니다** — 자식이 다시 호출하면
 * 부모와 무관한 별개 상태가 생겨 "링크를 눌러도 모바일 메뉴가 안 닫히는" 증상이 된다.
 * 섹션 앵커(`useScrollTo`)도 같은 이유로 부모가 처리한다. 그래서 이 컴포넌트는
 * **목적지 렌더만 책임지고, 부수효과는 전부 emit 으로 부모(AppHeader)에 위임**한다.
 */
import { computed } from 'vue';
import { cva } from 'class-variance-authority';
import { RouterLink } from 'vue-router';

import type { NavLink } from '#/config/nav';
import { cn } from '#/lib/utils';

interface Props {
  /** 렌더할 항목 — config/nav.ts 가 SSoT. */
  link: NavLink;
  /**
   * 표시 밀도. 마크업(4종 분기)은 공유하고 **스타일과 desc 노출 여부만** 가른다.
   *  - `mega`  : 데스크톱 메가메뉴 컬럼 — 라벨 + 설명 2줄, hover 배경
   *  - `panel` : 커머스 알약 헤더의 둥근 패널 컬럼 — 라벨 1줄, 네이비 잉크·골드 hover(`--lp-*` 고정값)
   *  - `panel-ws`: 워크스페이스 알약 헤더의 같은 패널 — 라벨 1줄, 시맨틱 토큰(`text-ink`/`hover:text-accent`)이라
   *                `data-theme='workspace'` 아래에서 그린으로 풀린다(구조는 `panel` 과 동일, 색만 다르다)
   *  - `mobile`: lg 미만 아코디언 — 라벨 1줄(설명 생략), 좌측 들여쓰기
   */
  variant: 'mega' | 'panel' | 'panel-ws' | 'mobile';
}

const props = defineProps<Props>();

const emit = defineEmits<{
  /** route·href 이동 직후 — 부모가 패널·모바일 메뉴를 닫는다. */
  navigate: [];
  /** 섹션 앵커 클릭 — 부모가 goToSection 으로 스크롤·라우팅을 처리한다. */
  section: [link: NavLink];
}>();

/** 배지 톤별 클래스 — 준비중(soon)만 뉴트럴, 나머지는 골드·블루. */
const BADGE_TONE: Record<string, string> = {
  ready: 'border-accent-line bg-accent-soft text-accent-deep',
  soon: 'border-line bg-bg-2 text-muted',
  read: 'border-brand-line-2 bg-brand-soft text-brand',
};

const rowVariants = cva('block w-full rounded-xl text-left transition-colors', {
  variants: {
    variant: {
      mega: 'px-3 py-2.5 hover:bg-bg-2',
      panel: 'py-0 hover:text-lp-gold-deep',
      'panel-ws': 'py-0 hover:text-accent',
      mobile: 'py-[11px] pl-3',
    },
  },
});

const labelVariants = cva('flex items-center gap-2 text-[14.5px]', {
  variants: {
    variant: {
      mega: 'font-bold',
      panel: 'text-[14px] font-medium',
      'panel-ws': 'text-[14px] font-medium',
      mobile: 'font-semibold',
    },
  },
});

/**
 * 목적지 종류 → 렌더할 요소.
 * `disabled` 는 목적지를 무시하고 비활성 표시가 되므로 가장 먼저 판정한다.
 */
const tag = computed(() => {
  if (props.link.disabled) return 'div';
  if (props.link.href) return 'a';
  if (props.link.section) return 'button';
  return RouterLink;
});

/** 요소별 목적지 속성 — 나머지 스타일·이벤트는 공유한다. */
const bindings = computed<Record<string, unknown>>(() => {
  const link = props.link;
  if (link.disabled) return { 'aria-disabled': 'true' };
  if (link.href) return { href: link.href, target: '_blank', rel: 'noopener' };
  if (link.section) return { type: 'button' };
  return { to: link.route };
});

const rowClass = computed(() =>
  cn(rowVariants({ variant: props.variant }), props.link.disabled && 'cursor-not-allowed'),
);

/** 비활성/일반 텍스트 색 — 준비중 항목은 본문색으로 낮춘다. */
const labelClass = computed(() =>
  cn(
    labelVariants({ variant: props.variant }),
    props.link.disabled || props.variant === 'mobile'
      ? 'text-body'
      : props.variant === 'panel'
        ? 'text-lp-navy'
        : props.variant === 'panel-ws'
          ? 'text-ink'
          : 'text-brand',
  ),
);

function onClick(): void {
  if (props.link.disabled) return;
  if (props.link.section) {
    emit('section', props.link);
    return;
  }
  emit('navigate');
}
</script>

<template>
  <component
    :is="tag"
    v-bind="bindings"
    :class="[rowClass, link.disabled ? 'opacity-60' : '']"
    @click="onClick"
  >
    <span :class="labelClass">
      {{ link.label }}
      <span
        v-if="link.badge"
        class="rounded-full border px-[7px] py-[1px] text-[11px] font-bold"
        :class="BADGE_TONE[link.badge.tone]"
      >
        {{ link.badge.text }}
      </span>
    </span>
    <!-- 설명은 메가메뉴에서만 — 모바일 아코디언은 라벨 1줄 유지(기존 동작). -->
    <span
      v-if="variant === 'mega' && link.desc"
      class="mt-[3px] block text-[12.5px] leading-[1.55] text-muted"
    >
      {{ link.desc }}
    </span>
  </component>
</template>
